import type { ReactNode } from "react";
import type { Membership, Workspace } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import {
  membershipAllowsIn,
  canEnter as membershipCanEnter,
  type Permission,
} from "../auth/permissions";
import { Notice } from "../ui/components";
import { useWorkspace } from "./workspace";

// The two halves of the building, named the way the switch in the top bar names them. One place
// for the words, so a page pointing at the other half calls it what the switch calls it.
export const workspaceNames: Record<Workspace, string> = {
  events: "Event workspace",
  bookings: "Booking workspace",
};

// One route per noun, one lens per workspace. A venue, an invoice, or a piece of kit is the same
// record whichever half of the building somebody is standing in, so the pages are not duplicated:
// each reads the open workspace from here and shows that half's sections, in that half's words,
// with what both halves share left in place. Sections the other half owns are folded away behind
// a pointer rather than shown, and the pointer only appears to somebody who could follow it.
export interface WorkspaceLens {
  workspace: Workspace;
  isEvents: boolean;
  isBookings: boolean;
  name: string;
  // The other half, when this person holds a role there. Null for somebody on one side only, so a
  // section that would point at it can simply stay hidden.
  other: Workspace | null;
  otherName: string | null;
  membership: Membership | undefined;
  // A capability asked of the role held in the open workspace alone.
  allows(permission: Permission): boolean;
  allowsIn(workspace: Workspace, permission: Permission): boolean;
  canEnter(workspace: Workspace): boolean;
  switchTo(workspace: Workspace): void;
}

export function useWorkspaceLens(): WorkspaceLens {
  const auth = useAuth();
  const { workspace, available, setWorkspace } = useWorkspace();
  const membership = auth.session?.selectedMembership;
  const other: Workspace = workspace === "events" ? "bookings" : "events";
  const otherAvailable = available.includes(other);
  return {
    workspace,
    isEvents: workspace === "events",
    isBookings: workspace === "bookings",
    name: workspaceNames[workspace],
    other: otherAvailable ? other : null,
    otherName: otherAvailable ? workspaceNames[other] : null,
    membership,
    allows: (permission) => membershipAllowsIn(membership, workspace, permission),
    allowsIn: (target, permission) => membershipAllowsIn(membership, target, permission),
    canEnter: (target) => membershipCanEnter(membership, target),
    switchTo: setWorkspace,
  };
}

// Where a section went. Rendered in place of the sections the other half owns, so somebody who
// came looking for pricing on the events side learns it is a booking matter and can go there in
// one click. Somebody who holds no role on that side is shown nothing: a door they cannot open is
// not a pointer, it is a distraction.
export function WorkspacePointer({ target, children }: { target: Workspace; children: ReactNode }) {
  const lens = useWorkspaceLens();
  if (!lens.canEnter(target)) return null;
  return (
    <Notice tone="info">
      {children}{" "}
      <button type="button" className="button ghost small" onClick={() => lens.switchTo(target)}>
        Open the {workspaceNames[target]}
      </button>
    </Notice>
  );
}
